import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Container, List, ListItem, ListItemButton, ListItemText,
  IconButton, Button, Divider, Paper
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ChatIcon from '@mui/icons-material/Chat';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ChatInterface from '../components/ChatInterface';
import { useAuth } from '../context/AuthContext';
import { api } from '../utils/api';

interface Conversation {
  id: number;
  title: string;
  created_at: string;
  updated_at: string;
}

const HomePage: React.FC = () => {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [activeConversationId, setActiveConversationId] = useState<number | null>(null);
  const [chatSessionKey, setChatSessionKey] = useState(0);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) navigate('/login');
  }, [isAuthenticated, authLoading, navigate]);

  const loadConversations = useCallback(async () => {
    try {
      const data = await api.get('/api/chat/conversations');
      if (data.success) {
        setConversations(data.conversations);
      }
    } catch (error) {
      console.error('Error loading conversations:', error);
    }
  }, []);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  const handleNewConversation = () => {
    setActiveConversationId(null);
    setChatSessionKey(prev => prev + 1);
  };

  const handleSelectConversation = (id: number) => {
    setActiveConversationId(id);
    setChatSessionKey(prev => prev + 1);
  };

  const handleConversationCreated = (id: number) => {
    setActiveConversationId(id);
    loadConversations();
  };

  const handleDeleteConversation = async (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const data = await api.delete(`/api/chat/conversations/${id}`);
      if (data.success) {
        setConversations(prev => prev.filter(c => c.id !== id));
        if (activeConversationId === id) {
          setActiveConversationId(null);
          setChatSessionKey(prev => prev + 1);
        }
      }
    } catch (error) {
      console.error('Error deleting conversation:', error);
    }
  };

  if (authLoading) return null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <Header />
      <Container maxWidth="lg" sx={{ flex: 1, display: 'flex', flexDirection: 'column', py: 3, overflow: 'hidden' }}>
        <Typography variant="h4" color="primary" sx={{ mb: 2, flexShrink: 0 }}>
          Medical Records Analyst
        </Typography>

        <Box sx={{ flex: 1, display: 'flex', gap: 2, overflow: 'hidden' }}>
          {/* Conversation sidebar */}
          <Paper
            elevation={1}
            sx={{
              width: 260,
              flexShrink: 0,
              display: { xs: 'none', md: 'flex' },
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <Box sx={{ p: 1.5 }}>
              <Button
                fullWidth
                variant="outlined"
                color="primary"
                size="small"
                startIcon={<AddIcon />}
                onClick={handleNewConversation}
              >
                New Chat
              </Button>
            </Box>
            <Divider />
            <List sx={{ flex: 1, overflow: 'auto', py: 0 }}>
              {conversations.length === 0 ? (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    No conversations yet
                  </Typography>
                </Box>
              ) : (
                conversations.map(conv => (
                  <ListItem
                    key={conv.id}
                    disablePadding
                    secondaryAction={
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={(e) => handleDeleteConversation(conv.id, e)}
                        sx={{ opacity: 0.4, '&:hover': { opacity: 1 } }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    }
                  >
                    <ListItemButton
                      selected={activeConversationId === conv.id}
                      onClick={() => handleSelectConversation(conv.id)}
                      sx={{ pr: 5 }}
                    >
                      <ChatIcon sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
                      <ListItemText
                        primary={conv.title || 'Untitled'}
                        primaryTypographyProps={{ noWrap: true, fontSize: '0.85rem' }}
                        secondary={new Date(conv.updated_at).toLocaleDateString()}
                        secondaryTypographyProps={{ fontSize: '0.7rem' }}
                      />
                    </ListItemButton>
                  </ListItem>
                ))
              )}
            </List>
          </Paper>

          {/* Chat area */}
          <Box sx={{ flex: 1, overflow: 'hidden' }}>
            <ChatInterface
              key={chatSessionKey}
              conversationId={activeConversationId || undefined}
              onConversationCreated={handleConversationCreated}
            />
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default HomePage;
